import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  Send,
  Plus,
  CheckCircle2,
  XCircle,
  Settings,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Key,
  Shield,
  DollarSign,
  MessageSquare,
  UserPlus,
  AlertTriangle,
  Save,
  X,
  Calendar,
  Clock,
  CalendarClock,
  Search,
  ArrowUpDown,
  Check,
  Phone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { impersonateClient } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLicenseDialogOpen, setIsLicenseDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name'); // name, created_at, expiration
  const [sortOrder, setSortOrder] = useState('asc'); // asc, desc
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive, not_configured
  
  // Password validation states
  const [passwordValidation, setPasswordValidation] = useState({
    min_length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    special: false,
    no_sequence: true,
    passwords_match: false
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [newClient, setNewClient] = useState({
    name: '',
    company_name: '',
    email: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    admin_mobile: ''
  });
  const [editClient, setEditClient] = useState({
    name: '',
    company_name: '',
    email: '',
    is_active: true,
    timezone: '',
    permissions: {
      cost_simulator_enabled: true,
      template_library_access: true,
      api_access: false,
      auth0_enabled: false
    }
  });
  const [licenseData, setLicenseData] = useState({
    start_date: '',
    expiration_date: '',
    notes: ''
  });
  const [expiringLicenses, setExpiringLicenses] = useState([]);

  // Password validation function
  const validatePassword = (password) => {
    const hasSequence = /(012|123|234|345|456|567|678|789|890|abc|bcd|cde|def)/i.test(password);
    
    setPasswordValidation({
      min_length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'/`~]/.test(password),
      no_sequence: !hasSequence,
      passwords_match: password === confirmPassword && password.length > 0
    });
  };

  // Update password validation when password or confirm changes
  useEffect(() => {
    validatePassword(newClient.admin_password);
  }, [newClient.admin_password, confirmPassword]);

  const isPasswordValid = () => {
    return passwordValidation.min_length &&
           passwordValidation.uppercase &&
           passwordValidation.lowercase &&
           passwordValidation.digit &&
           passwordValidation.special &&
           passwordValidation.no_sequence &&
           passwordValidation.passwords_match;
  };

  const fetchClients = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
      
      // Fetch expiring licenses
      try {
        const expiringResponse = await axios.get(`${API_URL}/licenses/expiring?days=30`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setExpiringLicenses(expiringResponse.data.clients || []);
      } catch (e) {
        // Ignore if endpoint not available
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Filter and sort clients
  const filteredAndSortedClients = useMemo(() => {
    let result = [...clients];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(client => 
        client.name?.toLowerCase().includes(query) ||
        client.company_name?.toLowerCase().includes(query) ||
        client.email?.toLowerCase().includes(query)
      );
    }
    
    // Apply status filter
    if (filterStatus !== 'all') {
      result = result.filter(client => {
        switch (filterStatus) {
          case 'active':
            return client.is_active && client.rcs_configured;
          case 'inactive':
            return !client.is_active;
          case 'not_configured':
            return client.is_active && !client.rcs_configured;
          default:
            return true;
        }
      });
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = (a.company_name || a.name || '').localeCompare(b.company_name || b.name || '');
          break;
        case 'created_at':
          comparison = new Date(a.created_at || 0) - new Date(b.created_at || 0);
          break;
        case 'expiration':
          const expA = a.license?.expiration_date ? new Date(a.license.expiration_date) : new Date('2099-12-31');
          const expB = b.license?.expiration_date ? new Date(b.license.expiration_date) : new Date('2099-12-31');
          comparison = expA - expB;
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return result;
  }, [clients, searchQuery, filterStatus, sortBy, sortOrder]);

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/clients`, newClient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client créé avec succès!');
      setIsDialogOpen(false);
      setNewClient({
        name: '',
        company_name: '',
        email: '',
        admin_name: '',
        admin_email: '',
        admin_password: ''
      });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du client');
    }
  };

  const handleOpenEditDialog = (client) => {
    setSelectedClient(client);
    setEditClient({
      name: client.name || '',
      company_name: client.company_name || '',
      email: client.email || '',
      is_active: client.is_active !== false,
      timezone: client.timezone || '',
      permissions: client.permissions || {
        cost_simulator_enabled: true,
        template_library_access: true,
        api_access: false
      }
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateClient = async () => {
    if (!selectedClient) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/clients/${selectedClient.id}`, editClient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client mis à jour avec succès!');
      setIsEditDialogOpen(false);
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleToggleActive = async (client) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/clients/${client.id}`, {
        ...client,
        is_active: !client.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(client.is_active ? 'Client désactivé' : 'Client activé');
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleImpersonate = async (client) => {
    try {
      const result = await impersonateClient(client.id);
      if (result.success) {
        toast.success(`Connecté en tant que ${result.client?.name || client.company_name}`);
        navigate('/dashboard');
      } else {
        toast.error(result.error || 'Impossible de se connecter en tant que ce client');
      }
    } catch (error) {
      toast.error('Erreur lors de la connexion');
    }
  };

  const handleDeleteClient = async () => {
    if (!selectedClient) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/clients/${selectedClient.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client supprimé');
      setIsDeleteDialogOpen(false);
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleConfigureRCS = (client) => {
    navigate('/rcs-agents', { state: { clientId: client.id, clientName: client.company_name } });
  };

  const handleViewBilling = (client) => {
    navigate('/billing', { state: { clientId: client.id } });
  };

  const handleOpenLicenseDialog = (client) => {
    setSelectedClient(client);
    // Parse existing license data if available
    const license = client.license || {};
    setLicenseData({
      start_date: license.start_date ? license.start_date.split('T')[0] : new Date().toISOString().split('T')[0],
      expiration_date: license.expiration_date ? license.expiration_date.split('T')[0] : '',
      notes: license.notes || ''
    });
    setIsLicenseDialogOpen(true);
  };

  const handleUpdateLicense = async () => {
    if (!selectedClient) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/licenses/${selectedClient.id}`, {
        start_date: licenseData.start_date ? new Date(licenseData.start_date).toISOString() : null,
        expiration_date: licenseData.expiration_date ? new Date(licenseData.expiration_date).toISOString() : null,
        notes: licenseData.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Licence mise à jour avec succès!');
      setIsLicenseDialogOpen(false);
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour de la licence');
    }
  };

  const handleExtendLicense = async (client, days) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/licenses/${client.id}/extend`, { days }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Licence prolongée de ${days} jours`);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la prolongation');
    }
  };

  const getLicenseStatus = (client) => {
    const license = client.license;
    if (!license || !license.expiration_date) {
      return { status: 'trial', label: 'Trial', color: 'bg-blue-100 text-blue-700' };
    }
    
    const expDate = new Date(license.expiration_date);
    const now = new Date();
    const daysRemaining = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining < 0) {
      return { status: 'expired', label: 'Expirée', color: 'bg-red-100 text-red-700', days: Math.abs(daysRemaining) };
    }
    if (daysRemaining <= 7) {
      return { status: 'critical', label: `${daysRemaining}j`, color: 'bg-red-100 text-red-700', days: daysRemaining };
    }
    if (daysRemaining <= 30) {
      return { status: 'warning', label: `${daysRemaining}j`, color: 'bg-yellow-100 text-yellow-700', days: daysRemaining };
    }
    return { status: 'active', label: `${daysRemaining}j`, color: 'bg-green-100 text-green-700', days: daysRemaining };
  };

  const getClientStatus = (client) => {
    if (!client.is_active) return { label: 'Désactivé', color: 'bg-red-100 text-red-700' };
    if (!client.rcs_configured) return { label: 'Non configuré', color: 'bg-yellow-100 text-yellow-700' };
    return { label: 'Actif', color: 'bg-green-100 text-green-700' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64" data-testid="loading">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="superadmin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Mon tableau de bord en Mode &gt; <span className="text-indigo-600">SuperAdmin</span>
            </h1>
            <p className="mt-2 text-slate-600">Gérez tous vos clients et leurs accès RCS</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full px-6 h-12 bg-indigo-700 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5" data-testid="create-client-button">
                <Plus className="mr-2 h-5 w-5" />
                Nouveau Client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="create-client-dialog">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Créer un nouveau client</DialogTitle>
                <DialogDescription>Ajoutez un nouveau client avec son administrateur</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateClient} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Nom du client</label>
                    <Input
                      value={newClient.name}
                      onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                      required
                      data-testid="client-name-input"
                      className="rounded-xl border-slate-200 h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Nom de l'entreprise</label>
                    <Input
                      value={newClient.company_name}
                      onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })}
                      required
                      data-testid="company-name-input"
                      className="rounded-xl border-slate-200 h-12"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email du client</label>
                  <Input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    required
                    data-testid="client-email-input"
                    className="rounded-xl border-slate-200 h-12"
                  />
                </div>
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <UserPlus className="h-4 w-4" />
                    Administrateur du client
                    <Badge className="bg-indigo-100 text-indigo-700 ml-2">ADMIN</Badge>
                  </h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Nom de l'admin</label>
                        <Input
                          value={newClient.admin_name}
                          onChange={(e) => setNewClient({ ...newClient, admin_name: e.target.value })}
                          required
                          data-testid="admin-name-input"
                          className="rounded-xl border-slate-200 h-12"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Mobile</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            type="tel"
                            value={newClient.admin_mobile}
                            onChange={(e) => setNewClient({ ...newClient, admin_mobile: e.target.value })}
                            placeholder="+33 6 XX XX XX XX"
                            data-testid="admin-mobile-input"
                            className="rounded-xl border-slate-200 h-12 pl-10"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Email de l'admin</label>
                      <Input
                        type="email"
                        value={newClient.admin_email}
                        onChange={(e) => setNewClient({ ...newClient, admin_email: e.target.value })}
                        required
                        data-testid="admin-email-input"
                        className="rounded-xl border-slate-200 h-12"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Mot de passe</label>
                        <Input
                          type="password"
                          value={newClient.admin_password}
                          onChange={(e) => setNewClient({ ...newClient, admin_password: e.target.value })}
                          required
                          data-testid="admin-password-input"
                          className="rounded-xl border-slate-200 h-12"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Confirmer le mot de passe</label>
                        <Input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          data-testid="admin-confirm-password-input"
                          className="rounded-xl border-slate-200 h-12"
                        />
                      </div>
                    </div>
                    {/* Password validation feedback */}
                    {newClient.admin_password && (
                      <div className="bg-slate-50 rounded-xl p-3 space-y-1">
                        <p className="text-xs font-medium text-slate-600 mb-2">Exigences du mot de passe :</p>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          <div className={`flex items-center gap-1 ${passwordValidation.min_length ? 'text-green-600' : 'text-slate-400'}`}>
                            {passwordValidation.min_length ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                            12 caractères min.
                          </div>
                          <div className={`flex items-center gap-1 ${passwordValidation.uppercase ? 'text-green-600' : 'text-slate-400'}`}>
                            {passwordValidation.uppercase ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                            Une majuscule
                          </div>
                          <div className={`flex items-center gap-1 ${passwordValidation.lowercase ? 'text-green-600' : 'text-slate-400'}`}>
                            {passwordValidation.lowercase ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                            Une minuscule
                          </div>
                          <div className={`flex items-center gap-1 ${passwordValidation.digit ? 'text-green-600' : 'text-slate-400'}`}>
                            {passwordValidation.digit ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                            Un chiffre
                          </div>
                          <div className={`flex items-center gap-1 ${passwordValidation.special ? 'text-green-600' : 'text-slate-400'}`}>
                            {passwordValidation.special ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                            Un caractère spécial
                          </div>
                          <div className={`flex items-center gap-1 ${passwordValidation.passwords_match ? 'text-green-600' : 'text-slate-400'}`}>
                            {passwordValidation.passwords_match ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                            Mots de passe identiques
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-full">
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    className="rounded-full bg-indigo-700" 
                    data-testid="submit-client-button"
                    disabled={!isPasswordValid()}
                  >
                    Créer le client
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Clients</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900" data-testid="total-clients">{clients.length}</p>
                </div>
                <div className="rounded-xl bg-indigo-50 p-3">
                  <Building2 className="h-8 w-8 text-indigo-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Clients Actifs</p>
                  <p className="mt-2 text-3xl font-bold text-green-600" data-testid="active-clients">
                    {clients.filter(c => c.is_active).length}
                  </p>
                </div>
                <div className="rounded-xl bg-green-50 p-3">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">RCS Configuré</p>
                  <p className="mt-2 text-3xl font-bold text-indigo-700" data-testid="rcs-configured">
                    {clients.filter(c => c.rcs_configured).length}
                  </p>
                </div>
                <div className="rounded-xl bg-indigo-50 p-3">
                  <Send className="h-8 w-8 text-indigo-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">API Activées</p>
                  <p className="mt-2 text-3xl font-bold text-purple-600" data-testid="api-enabled">
                    {clients.filter(c => c.permissions?.api_access).length}
                  </p>
                </div>
                <div className="rounded-xl bg-purple-50 p-3">
                  <Key className="h-8 w-8 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* License Alerts */}
        {expiringLicenses.length > 0 && (
          <Card className="rounded-2xl border-orange-200 bg-orange-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                <AlertTriangle className="h-5 w-5" />
                Licences à renouveler
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {expiringLicenses.slice(0, 5).map((lic) => (
                  <div key={lic.client_id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                    <div>
                      <span className="font-medium">{lic.company_name}</span>
                      <span className="text-sm text-slate-500 ml-2">
                        expire dans {lic.days_remaining} jour{lic.days_remaining > 1 ? 's' : ''}
                      </span>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        const client = clients.find(c => c.id === lic.client_id);
                        if (client) handleOpenLicenseDialog(client);
                      }}
                    >
                      <CalendarClock className="h-4 w-4 mr-1" />
                      Renouveler
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Clients List */}
        <Card className="rounded-2xl border-slate-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Liste des clients</CardTitle>
                <CardDescription>Gérez tous vos clients et leurs configurations RCS</CardDescription>
              </div>
              <Badge className="bg-slate-100 text-slate-700">{filteredAndSortedClients.length} client(s)</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search and Filters */}
            <div className="flex flex-wrap items-center gap-4 mb-6 pb-4 border-b border-slate-100">
              {/* Search */}
              <div className="relative flex-1 min-w-[250px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par nom, entreprise, email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-xl border-slate-200 h-11"
                  data-testid="search-clients"
                />
              </div>
              
              {/* Status Filter */}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[180px] rounded-xl h-11" data-testid="filter-status">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="inactive">Désactivé</SelectItem>
                  <SelectItem value="not_configured">Non configuré</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Sort By */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px] rounded-xl h-11" data-testid="sort-by">
                  <SelectValue placeholder="Trier par" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Nom (A-Z)</SelectItem>
                  <SelectItem value="created_at">Date de création</SelectItem>
                  <SelectItem value="expiration">Date d'expiration</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Sort Order */}
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="rounded-xl h-11 w-11"
                data-testid="sort-order"
              >
                <ArrowUpDown className={`h-4 w-4 ${sortOrder === 'desc' ? 'rotate-180' : ''} transition-transform`} />
              </Button>
            </div>

            <div className="space-y-3" data-testid="clients-list">
              {filteredAndSortedClients.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">
                    {searchQuery || filterStatus !== 'all' 
                      ? 'Aucun client ne correspond à vos critères' 
                      : 'Aucun client pour le moment'}
                  </p>
                  {!searchQuery && filterStatus === 'all' && (
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => setIsDialogOpen(true)}
                    >
                      Créer votre premier client
                    </Button>
                  )}
                </div>
              ) : (
                filteredAndSortedClients.map((client) => {
                  const licenseStatus = getLicenseStatus(client);
                  const clientStatus = getClientStatus(client);
                  return (
                    <div
                    key={client.id}
                    className="flex items-center justify-between rounded-xl border border-slate-100 bg-white p-4 hover:border-indigo-200 hover:shadow-sm transition-all"
                    data-testid={`client-${client.id}`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="rounded-lg bg-indigo-50 p-3">
                        <Building2 className="h-6 w-6 text-indigo-700" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{client.company_name}</p>
                        <p className="text-sm text-slate-500">{client.email}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span>Créé le {formatDate(client.created_at)}</span>
                          {client.license?.expiration_date && (
                            <>
                              <span>•</span>
                              <span>Expire le {formatDate(client.license.expiration_date)}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      {/* Status badges */}
                      <div className="flex items-center space-x-2">
                        {/* License Badge */}
                        <Badge className={`${licenseStatus.color} hover:${licenseStatus.color}`}>
                          <Clock className="mr-1 h-3 w-3" />
                          {licenseStatus.label}
                        </Badge>
                        
                        {client.rcs_configured ? (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            RCS
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-yellow-600 border-yellow-300">
                            <XCircle className="mr-1 h-3 w-3" />
                            Non configuré
                          </Badge>
                        )}
                        
                        {client.permissions?.api_access && (
                          <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">
                            <Key className="mr-1 h-3 w-3" />
                            API
                          </Badge>
                        )}
                        
                        <Badge className={clientStatus.color}>
                          {clientStatus.label}
                        </Badge>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEditDialog(client)}
                          data-testid={`edit-client-${client.id}`}
                          className="text-slate-600 hover:text-indigo-600"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`client-menu-${client.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuItem 
                              onClick={() => handleImpersonate(client)}
                              className="text-orange-600 focus:text-orange-700 focus:bg-orange-50"
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              Se connecter en tant que
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleOpenEditDialog(client)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Modifier le profil
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenLicenseDialog(client)}>
                              <CalendarClock className="mr-2 h-4 w-4" />
                              Gérer la licence
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleConfigureRCS(client)}>
                              <MessageSquare className="mr-2 h-4 w-4" />
                              Configurer RCS
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleViewBilling(client)}>
                              <DollarSign className="mr-2 h-4 w-4" />
                              Voir facturation
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleExtendLicense(client, 30)}>
                              <Calendar className="mr-2 h-4 w-4" />
                              +30 jours licence
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleToggleActive(client)}>
                              {client.is_active ? (
                                <>
                                  <XCircle className="mr-2 h-4 w-4" />
                                  Désactiver
                                </>
                              ) : (
                                <>
                                  <CheckCircle2 className="mr-2 h-4 w-4" />
                                  Activer
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedClient(client);
                                setIsDeleteDialogOpen(true);
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        {/* Edit Client Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Modifier le client
              </DialogTitle>
              <DialogDescription>
                Modifiez les informations et permissions de {selectedClient?.company_name}
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="info">Informations</TabsTrigger>
                <TabsTrigger value="permissions">Permissions</TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">Nom du client</Label>
                    <Input
                      id="edit-name"
                      value={editClient.name}
                      onChange={(e) => setEditClient({ ...editClient, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-company">Nom de l'entreprise</Label>
                    <Input
                      id="edit-company"
                      value={editClient.company_name}
                      onChange={(e) => setEditClient({ ...editClient, company_name: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editClient.email}
                    onChange={(e) => setEditClient({ ...editClient, email: e.target.value })}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">Statut du compte</p>
                    <p className="text-sm text-slate-500">
                      {editClient.is_active ? 'Le client peut se connecter et utiliser la plateforme' : 'Le client est désactivé'}
                    </p>
                  </div>
                  <Switch
                    checked={editClient.is_active}
                    onCheckedChange={(checked) => setEditClient({ ...editClient, is_active: checked })}
                  />
                </div>
                
                {/* Timezone selector */}
                <div className="space-y-2">
                  <Label>Fuseau horaire (pour la programmation SMS/RCS)</Label>
                  <Select
                    value={editClient.timezone || ''}
                    onValueChange={(value) => setEditClient({ ...editClient, timezone: value === 'global' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Utiliser le fuseau horaire global" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">Utiliser le fuseau horaire global</SelectItem>
                      <SelectItem value="Europe/Paris">Europe/Paris (France métropolitaine)</SelectItem>
                      <SelectItem value="Indian/Reunion">Indian/Reunion (La Réunion)</SelectItem>
                      <SelectItem value="Indian/Mauritius">Indian/Mauritius (Maurice)</SelectItem>
                      <SelectItem value="Indian/Mayotte">Indian/Mayotte (Mayotte)</SelectItem>
                      <SelectItem value="America/Guadeloupe">America/Guadeloupe</SelectItem>
                      <SelectItem value="America/Martinique">America/Martinique</SelectItem>
                      <SelectItem value="America/Cayenne">America/Cayenne (Guyane)</SelectItem>
                      <SelectItem value="Pacific/Noumea">Pacific/Noumea (Nouvelle-Calédonie)</SelectItem>
                      <SelectItem value="Pacific/Tahiti">Pacific/Tahiti (Polynésie française)</SelectItem>
                      <SelectItem value="UTC">UTC</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Ce fuseau horaire sera utilisé pour la planification des campagnes SMS et RCS de ce client.
                  </p>
                </div>
              </TabsContent>
              
              <TabsContent value="permissions" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <Key className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium">Accès API</p>
                        <p className="text-sm text-slate-500">Permet au client de créer des clés API</p>
                      </div>
                    </div>
                    <Switch
                      checked={editClient.permissions?.api_access || false}
                      onCheckedChange={(checked) => setEditClient({
                        ...editClient,
                        permissions: { ...editClient.permissions, api_access: checked }
                      })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <DollarSign className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium">Simulateur de coûts</p>
                        <p className="text-sm text-slate-500">Afficher le simulateur dans le menu</p>
                      </div>
                    </div>
                    <Switch
                      checked={editClient.permissions?.cost_simulator_enabled !== false}
                      onCheckedChange={(checked) => setEditClient({
                        ...editClient,
                        permissions: { ...editClient.permissions, cost_simulator_enabled: checked }
                      })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Shield className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">Bibliothèque de templates</p>
                        <p className="text-sm text-slate-500">Accès aux 42+ templates système</p>
                      </div>
                    </div>
                    <Switch
                      checked={editClient.permissions?.template_library_access !== false}
                      onCheckedChange={(checked) => setEditClient({
                        ...editClient,
                        permissions: { ...editClient.permissions, template_library_access: checked }
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg border-orange-200 bg-orange-50">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <Shield className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium">Login via Auth0</p>
                        <p className="text-sm text-slate-500">Activer la connexion SSO via Auth0 pour ce client</p>
                      </div>
                    </div>
                    <Switch
                      checked={editClient.permissions?.auth0_enabled || false}
                      onCheckedChange={(checked) => setEditClient({
                        ...editClient,
                        permissions: { ...editClient.permissions, auth0_enabled: checked }
                      })}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                <X className="mr-2 h-4 w-4" />
                Annuler
              </Button>
              <Button onClick={handleUpdateClient} className="bg-indigo-600">
                <Save className="mr-2 h-4 w-4" />
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Supprimer le client
              </DialogTitle>
              <DialogDescription>
                Êtes-vous sûr de vouloir supprimer <strong>{selectedClient?.company_name}</strong> ?
                Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
              <p className="text-sm text-red-700">
                <strong>Attention :</strong> Toutes les données associées à ce client seront supprimées :
              </p>
              <ul className="text-sm text-red-600 mt-2 list-disc list-inside">
                <li>Campagnes et messages</li>
                <li>Templates personnalisés</li>
                <li>Contacts et listes</li>
                <li>Historique de facturation</li>
              </ul>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                Annuler
              </Button>
              <Button variant="destructive" onClick={handleDeleteClient}>
                <Trash2 className="mr-2 h-4 w-4" />
                Supprimer définitivement
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* License Management Dialog */}
        <Dialog open={isLicenseDialogOpen} onOpenChange={setIsLicenseDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarClock className="h-5 w-5 text-indigo-600" />
                Gestion de la licence
              </DialogTitle>
              <DialogDescription>
                Configurez la licence pour {selectedClient?.company_name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Current License Status */}
              {selectedClient && (
                <div className={`p-4 rounded-lg ${
                  getLicenseStatus(selectedClient).status === 'expired' ? 'bg-red-50 border border-red-200' :
                  getLicenseStatus(selectedClient).status === 'critical' ? 'bg-red-50 border border-red-200' :
                  getLicenseStatus(selectedClient).status === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
                  getLicenseStatus(selectedClient).status === 'trial' ? 'bg-blue-50 border border-blue-200' :
                  'bg-green-50 border border-green-200'
                }`}>
                  <div className="flex items-center gap-2">
                    <Badge className={getLicenseStatus(selectedClient).color}>
                      {getLicenseStatus(selectedClient).label}
                    </Badge>
                    <span className="text-sm">
                      {getLicenseStatus(selectedClient).status === 'trial' && 'Mode d\'essai - aucune date d\'expiration'}
                      {getLicenseStatus(selectedClient).status === 'expired' && `Expirée depuis ${getLicenseStatus(selectedClient).days} jours`}
                      {getLicenseStatus(selectedClient).status === 'active' && `Expire dans ${getLicenseStatus(selectedClient).days} jours`}
                      {['critical', 'warning'].includes(getLicenseStatus(selectedClient).status) && `Expire dans ${getLicenseStatus(selectedClient).days} jours !`}
                    </span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="license-start">Date de début</Label>
                  <Input
                    id="license-start"
                    type="date"
                    value={licenseData.start_date}
                    onChange={(e) => setLicenseData({ ...licenseData, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="license-expiration">Date d'expiration</Label>
                  <Input
                    id="license-expiration"
                    type="date"
                    value={licenseData.expiration_date}
                    onChange={(e) => setLicenseData({ ...licenseData, expiration_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="license-notes">Notes</Label>
                <Input
                  id="license-notes"
                  placeholder="Notes sur cette licence..."
                  value={licenseData.notes}
                  onChange={(e) => setLicenseData({ ...licenseData, notes: e.target.value })}
                />
              </div>

              {/* Quick Actions */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-2">Actions rapides</p>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      const newDate = new Date();
                      newDate.setMonth(newDate.getMonth() + 1);
                      setLicenseData({ ...licenseData, expiration_date: newDate.toISOString().split('T')[0] });
                    }}
                  >
                    +1 mois
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      const newDate = new Date();
                      newDate.setMonth(newDate.getMonth() + 3);
                      setLicenseData({ ...licenseData, expiration_date: newDate.toISOString().split('T')[0] });
                    }}
                  >
                    +3 mois
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      const newDate = new Date();
                      newDate.setMonth(newDate.getMonth() + 6);
                      setLicenseData({ ...licenseData, expiration_date: newDate.toISOString().split('T')[0] });
                    }}
                  >
                    +6 mois
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      const newDate = new Date();
                      newDate.setFullYear(newDate.getFullYear() + 1);
                      setLicenseData({ ...licenseData, expiration_date: newDate.toISOString().split('T')[0] });
                    }}
                  >
                    +1 an
                  </Button>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                <p><strong>Note :</strong> À l'expiration de la licence :</p>
                <ul className="list-disc list-inside mt-1">
                  <li>Le client ne pourra plus envoyer de messages RCS</li>
                  <li>Les campagnes planifiées seront bloquées</li>
                  <li>Une notification sera envoyée automatiquement</li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsLicenseDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleUpdateLicense} className="bg-indigo-600">
                <Save className="mr-2 h-4 w-4" />
                Enregistrer la licence
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SuperAdminDashboard;
